import React, { useState, useEffect, useContext, useMemo } from "react";
import { Form, Input, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import "./users.css";
import secureLocalStorage from "react-secure-storage";

import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import CustomLoader from "../customLoader";
import TopContainerHeader from "../../../components/molecules/TopComponentHeader";
import TableContent from "../../../components/atoms/TableContent";
import AddUser from "./addUser";
import media from "../../../media";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  fetchRoleAsDropdownThunk,
  fetchUsersThunk,
  fetchHierarchyUsersAsDropdownThunk,
  fetchClientAsDopdownThunk,
  fetchDepartmentAsDopdownThunk,
} from "../../../store/thunks/usersThunk";
import { createAccessMap, hasPermission } from "../../../utils/permissions";
import { fetchAdditionalPermissionsAsDropdownThunk } from "../../../store/thunks/sectionsThunk";
import { AvatarCell } from "../../../utils/Constants";
import Unauthorized from "../../layouts/Unauthorized";

const Users = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  const currentPath = location.pathname.replace("/", "");
  const [tableForm] = Form.useForm();
  const rawAccess = useSelector((state) => state.auth.access);

  const { list, pageInfo, count, loading } = useSelector(
    (state) => state.muUsers,
  );
  const slsRole = secureLocalStorage.getItem("role");
  const muUsers = useSelector((state) => state.muUsers);
  const [allVps, setAllVps] = useState([]);
  const [allAvps, setAllAvps] = useState([]);
  const [allAms, setAllAms] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: Number(process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE || 50),
  });

  const shouldShowPagination = count > pagination.pageSize;
  const tablePagination = shouldShowPagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: count,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        locale: { items_per_page: "" },
        onChange: (page, pageSize) => {
          setPagination({ current: page, pageSize });
        },
      }
    : false;

  const accessMap = useMemo(() => {
    return createAccessMap(rawAccess);
  }, [rawAccess]);

  const hasViewAccess = hasPermission(accessMap, "users", "view");
  const userAdditionalPermissions = useSelector(
    (state) => state.auth.userInfo.additionalPermissions,
  );

  const canEditUserRole = userAdditionalPermissions?.some(
    (permission) => permission?.key === "editUserRole",
  );

  const canEditUserStatus = userAdditionalPermissions?.some(
    (permission) => permission?.key === "editUserStatus",
  );

  const canShowEditButton = hasPermission(accessMap, "users", "edit");

  //Search Filter
  const { Search } = Input;
  const [searchText, setSearchText] = useState("");

  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allManagers, setAllManagers] = useState([]);
  const [allTls, setAllTls] = useState([]);
  const [allAtls, setAllAtls] = useState([]);
  const [additionalPermissions, setAdditionalPermissions] = useState([]);
  const [showLoader, setShowLoader] = useState(true);

  const [statuses, setStatuses] = useState([
    { label: "ACTIVE", value: "ACTIVE" },
    { label: "INACTIVE", value: "INACTIVE" },
  ]);

  const [editingKey, setEditingKey] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const openAddModal = () => {
    setEditRecord(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  const renderUserCell = (user) => {
    return <AvatarCell avt={user} />;
  };

  const renderMultiValueCell = (items, scope, allLabel) => {
    if (scope === "ALL") {
      return <span className="rowStyle no-wrap profileText">{allLabel}</span>;
    }

    if (!Array.isArray(items) || !items.length) {
      return <span>-</span>;
    }
    const displayText = items
      .map((item) => {
        const deptNames = (item.departments || [])
          .map((d) => d.name)
          .join(", ");

        return `${item.name}${deptNames ? ` - ${deptNames}` : ""}`;
      })
      .join(", ");
    const itemNames = items
      .map((item) => item?.name)
      .filter(Boolean)
      .join(", ");

    const maxLength = 50;

    const truncatedText =
      displayText.length > maxLength
        ? displayText.substring(0, maxLength) + "..."
        : displayText;

    return (
      <span className="rowStyle no-wrap profileText" title={displayText}>
        {truncatedText}
      </span>
    );
  };
  const renderClientCell = (clients) => {
    if (!Array.isArray(clients) || clients.length === 0) {
      return <span>-</span>;
    }

    const clientNames = clients
      .map((client) => client?.name)
      .filter(Boolean)
      .join(", ");

    const maxLength = 5;

    const truncatedText =
      clientNames.length > maxLength
        ? clientNames.substring(0, maxLength) + "..."
        : clientNames;

    return (
      <span className="rowStyle no-wrap profileText" title={clientNames}>
        {truncatedText}
      </span>
    );
  };

  const additionalPermissionColumn = {
    title: () => {
      return (
        <div className="tableTitle">
          <img src={media.departmentIcon.svg} alt="icon" />
          Additional Permissions
        </div>
      );
    },

    dataIndex: "additionalPermissions",

    editable: true,

    render: (permissions) => renderMultiValueCell(permissions),
  };

  const columns = [
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.auditId.svg} alt="icon" />
            ID
          </div>
        );
      },
      dataIndex: "id",

      render: (text) => <span className="rowstyle">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.transactionId.svg} alt="icon" />
            Emp ID
          </div>
        );
      },
      dataIndex: "empID",

      editable: true,
      render: (text, row) => <span className="rowstyle">{text}</span>,
    },

    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentIcon.svg} alt="icon" />
            Name
          </div>
        );
      },
      dataIndex: "name",

      editable: true,
      render: (text, row) => <span className="rowstyle">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentIcon.svg} alt="icon" />
            Email
          </div>
        );
      },
      dataIndex: "email",

      editable: true,
      render: (text) => <span className="rowstyle">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentName.svg} alt="icon" />
            Role
          </div>
        );
      },
      dataIndex: "role",

      editable: true,
      render: (text) => (
        <span className="rowstyle">
          {text && text.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.supervisor.svg} alt="icon" />
            VP
          </div>
        );
      },
      dataIndex: "vp",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,

      render: renderUserCell,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.supervisor.svg} alt="icon" />
            AVP
          </div>
        );
      },
      dataIndex: "avp",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,

      render: renderUserCell,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.supervisor.svg} alt="icon" />
            Manager
          </div>
        );
      },
      dataIndex: "manager",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,

      render: renderUserCell,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.supervisor.svg} alt="icon" />
            AM
          </div>
        );
      },
      dataIndex: "am",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,

      render: renderUserCell,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.supervisor.svg} alt="icon" />
            TL
          </div>
        );
      },
      dataIndex: "tl",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,

      render: renderUserCell,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.supervisor.svg} alt="icon" />
            ATL
          </div>
        );
      },
      dataIndex: "atl",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,
      render: renderUserCell,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.auditor.svg} alt="icon" />
            Clients
          </div>
        );
      },
      dataIndex: "client",

      editable: true,
      // render: (text) => <span className="rowstyle">{text ? text : ""}</span>,
      render: (_, row) =>
        renderMultiValueCell(
          row.client,
          row.clientScope,
          "All Clients",
          row.clientDepartment,
        ),
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentIcon.svg} alt="icon" /> Departments
          </div>
        );
      },
      dataIndex: "department",
      editable: true,
      render: (_, row) =>
        renderMultiValueCell(
          row.department,
          row.departmentScope,
          "All Departments",
        ),
    },
    ...(slsRole === process.env.REACT_APP_AROLE
      ? [additionalPermissionColumn]
      : []),

    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentStatus.svg} alt="icon" />
            Status
          </div>
        );
      },
      dataIndex: "status",

      editable: true,

      render: (text, row) => (
        <span
          className={`cusStatus ${
            text?.toLowerCase() === "active" ? "cusActive" : "cusInActive"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentDate.svg} alt="icon" />
            Created At
          </div>
        );
      },
      dataIndex: "createdAt",

      render: (text) => <span className="rowstyle">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentDate.svg} alt="icon" />
            Updated At
          </div>
        );
      },
      dataIndex: "updatedAt",
      render: (text) => <span className="rowstyle ">{text || "-"}</span>,
    },
    {
      title: "Action",
      dataIndex: "action",

      render: (_, record) => {
        return (
          hasPermission(accessMap, "users", "edit") &&
          canShowEditButton && (
            <Typography.Link
              disabled={editingKey !== ""}
              onClick={() => openEditModal(record)}
              className="tableBtn bfont"
            >
              <i className="fa fa-pencil-square-o" aria-hidden="true"></i> Edit
            </Typography.Link>
          )
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editing: isEditing(record) ? true : undefined,
        cellconfig: {
          inputType: "text",
          dataIndex: col.dataIndex,
          title: typeof col.title === "string" ? col.title : undefined,
          roles,
          clients,
          allTls,
          allAtls,
          statuses,
        },
      }),
    };
  });

  // Fetch Additional Permissions
  const getAdditionalPermissions = () => {
    const payload = {
      route: "permissions",
      action: "fetchAdditionalPermissions",
    };
    dispatch(fetchAdditionalPermissionsAsDropdownThunk(payload))
      .unwrap()
      .then((response) => {
        const temp = [];

        response?.map((additionalPermission, index) => {
          temp.push({
            label: additionalPermission?.name,
            value: additionalPermission?._id,
          });
        });

        setAdditionalPermissions(temp);
      })
      .catch((error) => {});
  };

  /*begin::getHierarchyUsers will fetch the list of Tls*/

  const getHierarchyUsers = async () => {
    const payload = {
      route: "users",
      action: "findAll",
      fields: ["vp", "avp", "manager", "am", "tl", "atl", "qc"],
    };
    await dispatch(fetchHierarchyUsersAsDropdownThunk(payload))
      .unwrap()
      .then((response) => {
        const storeAllVps = [];

        const getAllVps = response.filter((user) => user?.role === "vp");

        getAllVps?.map((user) => {
          storeAllVps.push({
            label: user?.name + " (EmpID: " + user?.empID + ")",
            value: user?._id,
          });
        });

        setAllVps(storeAllVps);

        const storeAllAvps = [];

        const getAllAvps = response.filter((user) => user?.role === "avp");
        getAllAvps?.map((user) => {
          storeAllAvps.push({
            label: user?.name + " (EmpID: " + user?.empID + ")",

            value: user?._id,

            vp: user?.vp?._id || user?.vp,
          });
        });

        setAllAvps(storeAllAvps);

        const storeAllMgrs = [];
        const getAllMgrs = response.filter(
          (user) =>
            user?.role === "manager" || (user?.role === "qc" && !user?.manager),
        );
        getAllMgrs?.map((user, index) => {
          storeAllMgrs.push({
            label: user?.name + " (EmpID: " + user?.empID + ")",

            value: user?._id,

            vp: user?.vp?._id || user?.vp,

            avp: user?.avp?._id || user?.avp,
          });
        });
        setAllManagers(storeAllMgrs);

        const storeAllAms = [];

        const getAllAms = response.filter((user) => user?.role === "am");

        getAllAms?.map((user) => {
          storeAllAms.push({
            label: user?.name + " (EmpID: " + user?.empID + ")",

            value: user?._id,

            vp: user?.vp?._id || user?.vp,

            avp: user?.avp?._id || user?.avp,

            manager: user?.manager?._id || user?.manager,
          });
        });

        setAllAms(storeAllAms);

        const storeAllTls = [];
        const getAllTls = response.filter((user) => user?.role === "tl");
        getAllTls?.map((user, index) => {
          storeAllTls.push({
            label: user?.name + " (EmpID: " + user?.empID + ")",

            value: user?._id,

            vp: user?.vp?._id || user?.vp,

            avp: user?.avp?._id || user?.avp,

            manager: user?.manager?._id || user?.manager,

            am: user?.am?._id || user?.am,
          });
        });
        setAllTls(storeAllTls);

        const storeAllATls = [];
        const getAllATls = response.filter((user) => user?.role === "atl");

        getAllATls?.map((user, index) => {
          storeAllATls.push({
            label: user?.name + " (EmpID: " + user?.empID + ")",

            value: user?._id,

            vp: user?.vp?._id || user?.vp,

            avp: user?.avp?._id || user?.avp,

            manager: user?.manager?._id || user?.manager,

            am: user?.am?._id || user?.am,

            tl: user?.tl?._id || user?.tl,
          });
        });
        setAllAtls(storeAllATls);
      })
      .catch((error) => {});
  };
  /*end::getHierarchyUsers will fetch the list of Clients*/

  /*begin::getClients will fetch the list of Clients*/
  const getClients = async () => {
    const payload = { route: "clients", action: "findAll" };

    await dispatch(fetchClientAsDopdownThunk(payload))
      .unwrap()
      .then((response) => {
        const temp = [
          {
            label: "All Clients",
            value: "ALL",
          },
        ];

        response?.map((client, index) => {
          const clientName =
            client?.departmentName && client?.name
              ? `${client?.name} - ${client?.departmentName}`
              : client?.name;
          temp.push({ label: clientName, value: client?._id });
        });
        setClients(temp);
      })
      .catch((error) => {});
  };
  /*end::getClients will fetch the list of Clients*/

  /*begin::getDepartments will fetch the list of Departments*/
  const getDepartments = async () => {
    const payload = { route: "departments", action: "findAll" };

    await dispatch(fetchDepartmentAsDopdownThunk(payload))
      .unwrap()
      .then((response) => {
        const temp = [
          {
            label: "All Departments",
            value: "ALL",
          },
        ];
        response?.map((department, index) => {
          temp.push({ label: department?.name, value: department?._id });
        });
        setDepartments(temp);
      })
      .catch((error) => {});
  };
  /*end::getDepartments will fetch the list of Departments*/

  /*begin::getRoles will fetch the list of Roles*/
  const getRoles = async () => {
    const payload = {};
    await dispatch(fetchRoleAsDropdownThunk(payload))
      .unwrap()
      .then((response) => {
        const temp = [];
        response?.map((role, index) => {
          temp.push({ label: role?.name, value: role?.name?.toLowerCase() });
        });
        setRoles(temp);
      })
      .catch((error) => {});
  };
  /*end::getRoles will fetch the list of Roles*/

  // Fetch users
  useEffect(() => {
    const fetchData = async () => {
      try {
        setShowLoader(true);
        const hasSearchedValue = secureLocalStorage.getItem("hasSearchedValue")
          ? "yes"
          : "";
        const searchKeyword = secureLocalStorage.getItem("searchKeyword") || "";

        await dispatch(
          fetchUsersThunk({
            searchIn: currentPath,
            callActionFrom: currentPath,
            hasSearchedValue,
            searchKeyword,
            route: currentPath,
            pagination: {
              current: pagination.current,
              page: pagination.current,
              pageSize: pagination.pageSize,
            },
            listing: "yes",
          }),
        ).unwrap();
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setShowLoader(false);
      }
    };

    fetchData();
  }, [currentPath, pagination.current, pagination.pageSize, dispatch]);

  useEffect(() => {
    //getUserList();
    getRoles();
    getClients();
    getDepartments();
    getAdditionalPermissions();
    getHierarchyUsers();
  }, []);

  // Filter the storeList based on the searchText

  // const searchFilterData =
  //   globalState.list &&
  //   globalState.list.filter((item) => {
  //     const role = item.role ? item.role.toLowerCase() : "";
  //     const empID = item.empID ? item.empID : "";
  //     const name = item.name ? item.name.toLowerCase() : "";
  //     const email = item.email ? item.email : "";
  //     const client = item.client ? item.client.toLowerCase() : "";
  //     const status = item.status ? item.status.toLowerCase() : "";

  //     return (
  //       role.includes(searchText.toLowerCase()) ||
  //       empID === Number(searchText) ||
  //       name.includes(searchText.toLowerCase()) ||
  //       email.includes(searchText) ||
  //       client.includes(searchText.toLowerCase()) ||
  //       status.includes(searchText.toLowerCase())
  //     );
  //   });

  const isEditing = (record) => record.key === editingKey;

  if (!hasViewAccess) {
    return <Unauthorized />;
  }
  return (
    <div className="all-content">
      <CustomLoader showLoader={showLoader} />
      <TopContainerHeader
        sectionTitle="Users"
        sectionDescription={showLoader ? "Loading..." : `${count || 0} Found`}
        hasSectionButton={
          hasPermission(accessMap, "users", "add") &&
          slsRole === process.env.REACT_APP_AROLE &&
          true
        }
        sectionButtons={
          hasPermission(accessMap, "users", "add") &&
          slsRole === process.env.REACT_APP_AROLE && [
            {
              isVisible: true,
              itemType: "add",
              itemName: "Add",
              onClick: openAddModal,
            },
          ]
        }
        hasFilter={false}
        buttonTitle="Add New"
        FormComponent={AddUser}
        formTitle={editRecord ? "Edit User" : "Add User"}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        formProps={{
          editData: editRecord,
          clients,
          departments,
          roles,
          additionalPermissions,
          statuses,
          allVps,
          allAvps,
          allManagers,
          allAms,
          allTls,
          allAtls,
          canEditUserRole,
          canEditUserStatus,
          onSuccess: () => {
            dispatch(
              fetchUsersThunk({
                searchIn: currentPath,
                callActionFrom: currentPath,
                hasSearchedValue: secureLocalStorage.getItem("hasSearchedValue")
                  ? "yes"
                  : "",
                searchKeyword:
                  secureLocalStorage.getItem("searchKeyword") || "",
                route: currentPath,
                pagination: {
                  current: pagination.current,
                  page: pagination.current,
                  pageSize: pagination.pageSize,
                },
              }),
            );
            setModalOpen(false);
          },
        }}
      />

      <div className="all-table-holder">
        <Form form={tableForm} component={false}>
          <TableContent
            rowClassName="editable-row"
            bordered={false}
            columns={mergedColumns}
            dataSource={list}
            pagination={tablePagination}
          />
        </Form>
      </div>
    </div>
  );
};
export default Users;
